import { type ReactNode } from 'react';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import {
  createPortfolioQueryClient,
  PortfolioClientProvider,
  prefetchPortfolio,
} from '@personal-portfolio/queries';
import { appConfig, portfolioClient } from '../config';

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

const queryClient = createPortfolioQueryClient({ isDev: appConfig.isDev });

const persister = createSyncStoragePersister({
  storage: window.localStorage,
  key: 'portfolio-query-cache',
});

// Warm all portfolio queries only AFTER the persisted cache has been restored
// (onSuccess fires once per restore) — prefetching from a mount effect would
// race the async restore and refetch all 7 queries on every load.
const handleRestoreSuccess = () => {
  void prefetchPortfolio(queryClient, portfolioClient);
};

export const QueryProvider = ({ children }: { children: ReactNode }) => (
  <PersistQueryClientProvider
    client={queryClient}
    persistOptions={{
      persister,
      // 7 days so offline visitors keep content across a week of absence
      // (query gcTime matches — entries older than gcTime are dropped on
      // restore). The per-release buster still discards the cache on every
      // deploy; acceptable tradeoff: content re-fetches on the next online
      // visit and static defaults cover the copy until then.
      maxAge: WEEK_MS,
      buster: appConfig.appVersion,
    }}
    onSuccess={handleRestoreSuccess}
  >
    <PortfolioClientProvider client={portfolioClient}>
      {children}
      {appConfig.isDev ? <ReactQueryDevtools initialIsOpen={false} /> : null}
    </PortfolioClientProvider>
  </PersistQueryClientProvider>
);
